import {
  Box,
  Button,
  CrudMode,
  DataArray,
  DataDisplay,
  Grid,
  GridBreak,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { Input, Select } from "components";
import { District, DistrictEditResource, DistrictViewResource, School } from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const DistrictPageBody = (props: {
  crudMode: CrudMode;
  district: District;
  setDistrict: (value: District) => void;
  viewResource: DistrictViewResource;
  editResource: DistrictEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const nameField = () => {
    const propertyName = pn<District>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.district.name}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.district.name}
        onChange={(e) =>
          props.setDistrict(
            produce(props.district, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const schoolsField = () => {
    const propertyName = pn<District>("schools");
    const property = properties.find((x) => x.propertyName === propertyName);
    const getSchoolLabel = (school: School) => (
      <Stack direction="row" spacing="sm" alignItems={"center"}>
        <Typography variant="inherit">{school.name}</Typography>
        <Typography variant="caption" color={(theme) => theme.palette.warning.main}>
          {school.inactive && "Inactive"}
        </Typography>
      </Stack>
    );

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.district.schools?.map((x) => getSchoolLabel(x))} />
      </DataDisplay>
    ) : (
      <Stack alignItems="flex-end">
        <Select
          multiple
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.district.schools}
          options={props.editResource.schools}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {getSchoolLabel(option)}
            </Box>
          )}
          onChange={(e, values) =>
            props.setDistrict(
              produce(props.district, (draft) => {
                draft.schools = values;
              })
            )
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
        <Button
          onClick={() =>
            props.setDistrict(
              produce(props.district, (draft) => {
                draft.schools = props.editResource.schools;
              })
            )
          }
          size="small"
          textTransform="none"
        >
          + Select all schools
        </Button>
      </Stack>
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {nameField()}
      </Grid>

      <GridBreak />

      <Grid item xs={fullFieldBreakpoints}>
        {schoolsField()}
      </Grid>
    </Grid>
  );
};
